import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { useNavigate } from 'react-router';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import Link from 'components/Link';
import Alert from 'components/Alert';
import { useGlobalState } from 'hooks/useGlobalState';
import { useRecaptcha } from 'hooks/useRecaptcha';
import { requiredErrorMsg, unexpectedServerErrorMsg } from 'utils/message';
import { stopPoll } from 'hostedFreePages/Onboarding/util';
import Button from 'components/Button';

import InputField from './InputField';
import CheckBoxField from './CheckboxField';
import styles from './SignupForm.module.scss';
import { autoFill } from '../util';

const RECAPTCHA_CONTAINER_ID = 'xm_recaptcha';
// TODO: update regex and ask prodComm for the error message once we finialize the rules for company name
export const companyNameRegex = /^[a-zA-Z0-9\s-]{1,100}$/;
export const companyNameValidationErrMsg = 'Must be a valid company name.';
export const invalidCompanyNameCharsMsg =
  'Can only include alphanum, dashes, and spaces';

export const userNameRegex = /^[a-zA-Z0-9\s-_']+$/;
export const invalidUserNameMsg =
  'Cannot contain special characters (e.g., %&<>#!)';

export const emailValidationErrMsg = 'Must be a valid email.';
export const companyNameExistedMsg = 'The company name is not available.';

export const emailExistedMsg = (
  <>
    This email is already in use. Use a different email or contact
    <Link
      className={styles.supportLink}
      href="https://support.xmatters.com/hc/en-us/requests/new"
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      {' '}
      Support{' '}
    </Link>
    for help logging into your instance.
  </>
);

const ZOOMINFO_CONTAINER_ID = 'zi-return-fields';
// SIGNUP_FORM_ID value must match what is configured in ZoomInfo FormComplete for the "provisioning-customer-category" form
const SIGNUP_FORM_ID = 'provision-signup';

const postForm = (formData) =>
  axios.post(
    `xm/free/api/registration?g-recaptcha-response=${formData.catchaResponse}`,
    {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      companyName: formData.companyName.trim(),
      email: formData.emailAddress.toLowerCase(),
      superRegion: formData.region.toLowerCase(),
      referralId: window?.location?.href,
      fcFullResponse: formData['fcFullResponse'].trim(),
      nbStatus: formData['nbStatus'].trim(),
      signupStage: 'begin',
      timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
    },
  );
export default function SignupForm({ formHeader }) {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    errors,
    setError,
    formState: { isSubmitting },
  } = useForm();

  const navigate = useNavigate();
  const [{ signup }, dispatch] = useGlobalState();

  const successCallback = (response) =>
    handleSubmit((data) => onSubmit({ ...data, catchaResponse: response }))();
  const { recaptchaLoaded, recaptchaWidget } = useRecaptcha(
    RECAPTCHA_CONTAINER_ID,
    successCallback,
  );

  const { error: signupError } = signup;
  const canSubmit = !isSubmitting && isEmpty(errors) && recaptchaLoaded;
  const [apiError, setApiError] = useState(null);
  const clearApiError = () => setApiError(null);

  useEffect(() => {
    if (signupError) {
      setApiError(unexpectedServerErrorMsg);
    }
  }, [signupError, setApiError]);

  useEffect(() => {
    dispatch({
      type: 'SET_WAITING_SCREEN_VISIBLE',
      payload: false,
    });
    stopPoll();
  }, [dispatch]);

  useEffect(() => {
    const devMode =
      (window.location.href.indexOf('devMode=true') > 0 ||
        window.location.href.indexOf('dev.xmatters.com') > 0 ||
        window.location.href.indexOf('tst.xmatters.com') > 0) &&
      window.location.href.indexOf('devMode=false') === -1;
    window._zi = {
      formId: 'aWtTJXQX6CzK3VRYCT9g',
      development: devMode,
    };
    window._zi.callbacks = {
      onMatch: function (data) {
        const nbStatus = document
          .getElementsByName('emailAddress')[0]
          .getAttribute('data-zi-neverbounce-status');
        document.getElementById('nbStatus').value = nbStatus;
        document.getElementById('fcFullResponse').value = JSON.stringify(data);
      },
    };
    const zoomInfoScriptObject = document.createElement('script');
    zoomInfoScriptObject.type = 'text/javascript';
    zoomInfoScriptObject.async = true;
    zoomInfoScriptObject.src = 'https://ws-assets.zoominfo.com/formcomplete.js';
    const scriptObject = document.getElementsByTagName('script')[0];
    scriptObject.parentNode.insertBefore(zoomInfoScriptObject, scriptObject);
  }, []);

  const onSubmit = (data) => {
    clearApiError();
    return postForm(data)
      .then((res) => {
        dispatch({
          type: 'SIGNUP_BEGIN',
          payload: {
            completeSignupURL: res.headers.location,
            region: data.region,
          },
        });

        navigate('/onboarding');
      })
      .catch((error) => {
        const errorCode = error?.response?.status;
        if (errorCode === 409) {
          setError('emailAddress', {
            type: 'duplicate',
            message: emailExistedMsg,
          });
        } else if (errorCode >= 500) {
          setApiError(unexpectedServerErrorMsg);
        } else
          setApiError(
            error?.response?.data?.message || unexpectedServerErrorMsg,
          );
      });
  };

  const executeCaptcha = (e) => {
    e.preventDefault();
    // In test environment, bypass recaptcha and call success callback directly
    // because the reCAPTCHA widget doesn't load properly in jsdom
    if (process.env.NODE_ENV === 'test') {
      successCallback('test-recaptcha-token');
      return;
    }
    if (recaptchaWidget !== null) {
      window.grecaptcha.reset(recaptchaWidget);
      window.grecaptcha.execute(recaptchaWidget);
    }
  };

  const setEmail = (e) => {
    let emailStr = e.target.value.trim();
    setValue('emailAddress', emailStr);
  };

  useEffect(() => {
    const autoFillData = autoFill();
    for (const key in autoFillData) {
      if (autoFillData[key]) setValue(key, autoFillData[key]);
    }
  }, [setValue]);

  return (
    <form
      id={SIGNUP_FORM_ID}
      className={styles.signupForm}
      onSubmit={executeCaptcha}
    >
      {apiError && (
        <Alert
          error
          role="alert"
          className={styles.alertBox}
          data-testid="signupForm_alert"
        >
          <span>{apiError}</span>
        </Alert>
      )}
      <h3 className={styles.h2}>{formHeader}</h3>
      <InputField
        autoComplete="given-name"
        error={errors.firstName}
        fieldName="firstName"
        inputRef={register({
          pattern: {
            value: userNameRegex,
            message: invalidUserNameMsg,
          },
          validate: (value) => !!value.trim() || requiredErrorMsg,
        })}
        labelText="First Name"
        maxLength={100}
        placeholder="Morgan"
        testid="signupForm_firstName"
        width="halfWidth"
      />
      <InputField
        autoComplete="family-name"
        error={errors.lastName}
        fieldName="lastName"
        inputRef={register({
          pattern: {
            value: userNameRegex,
            message: invalidUserNameMsg,
          },
          validate: (value) => !!value.trim() || requiredErrorMsg,
        })}
        labelText="Last Name"
        maxLength={100}
        placeholder="Smith"
        testid="signupForm_lastName"
        width="halfWidth"
      />
      <InputField
        autoComplete="email"
        error={errors.emailAddress}
        fieldName="emailAddress"
        inputRef={register({
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,16}$/i,
            message: emailValidationErrMsg,
          },
          validate: (value) => !!value.trim() || requiredErrorMsg,
        })}
        labelText="Company Email"
        maxLength={254}
        placeholder="Your free URL will be delivered here"
        testid="signupForm_emailAddress"
        onChange={setEmail}
      />
      <InputField
        autoComplete="organization"
        error={errors.companyName}
        fieldName="companyName"
        inputRef={register({
          pattern: {
            value: companyNameRegex,
            message: invalidCompanyNameCharsMsg,
          },
          validate: (value) => !!value.trim() || requiredErrorMsg,
        })}
        labelText="Company"
        maxLength={100}
        placeholder="eg: Acme Corp"
        testid="signupForm_companyName"
      />
      <div className={classnames(styles.topLabel, styles.fullWidth)}>
        <label htmlFor="region">Region</label>
        <select
          name="region"
          className={classnames(styles.field, styles.selectField)}
          defaultValue=""
          ref={register({ required: requiredErrorMsg })}
          required
          data-testid="signupForm_region"
        >
          <option value="" disabled>
            Select
          </option>
          <option value="NA">Americas</option>
          <option value="EMEA">EMEA</option>
          <option value="APAC">APAC</option>
        </select>
      </div>

      <p className={classnames(styles.textWrap, styles.fullWidth)}>
        Your region helps determine the best location to host your instance.
      </p>

      <div className={classnames(styles.checkboxContainer, styles.fullWidth)}>
        <Controller
          name="consent"
          control={control}
          defaultValue={false}
          rules={{ required: requiredErrorMsg }}
          render={(props) => (
            <CheckBoxField {...props} error={!!errors.consent} />
          )}
        />
        {errors.consent && (
          <span role="alert" className={styles.errorText}>
            {errors.consent.message}
          </span>
        )}
      </div>

      <div id={ZOOMINFO_CONTAINER_ID}>
        <input type="hidden" name="jobTitle" />
        <input type="hidden" name="ziCompanyId" />
        <input type="hidden" name="companyWebsite" />
        <input type="hidden" name="companyPhone" />
        <input type="hidden" name="annualRevenue" />
        <input type="hidden" name="employeeCount" />
        <input type="hidden" name="city" />
        <input type="hidden" name="state" />
        <input type="hidden" name="country" />
        <input type="hidden" name="zipCode" />
        <input type="hidden" name="industry" />
        <input type="hidden" name="department" />
        <input type="hidden" name="jobFunction" />
        <input type="hidden" name="managementLevel" />
        <input type="hidden" name="ziContactId" />
        <input type="hidden" name="ziContactFirst" />
        <input type="hidden" name="ziContactLast" />
        <input type="hidden" name="ziCompany" />
        <input type="hidden" name="ziCAS" />
        <input type="hidden" name="ziCAG" />
        <input
          type="hidden"
          ref={register()}
          id="fcFullResponse"
          name="fcFullResponse"
        />
        <input type="hidden" ref={register()} id="nbStatus" name="nbStatus" />
      </div>

      <div className={styles.fullWidth}>
        <Button
          className={styles.button}
          type="submit"
          color="transparent"
          disabled={!canSubmit}
          data-testid="signupForm_btn"
        >
          {isSubmitting ? 'Please wait' : 'Get started'}
        </Button>
      </div>

      <div id={RECAPTCHA_CONTAINER_ID} />
    </form>
  );
}

SignupForm.propTypes = {
  formHeader: PropTypes.string,
};

SignupForm.defaultValue = {
  formHeader: '',
};
